// src/components/workflow/WorkflowActions.tsx
import React, { useState, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  Send,
  Archive,
  Play,
  Flag,
  ArrowRight,
  Clock,
  RotateCcw,
  Building2,
  User,
  Shield,
  Gavel} from 'lucide-react';
import { Button, Modal, Textarea, Badge } from '../ui';
import { AvailableTransition, LaporanMasalah } from '../../types';

interface Props {
  transitions: AvailableTransition[];
  loading: boolean;
  laporan?: Partial<LaporanMasalah>;
  onChangeStatus: (toState: string, notes?: string) => Promise<any>;
}

// ========== CONSTANTS ==========
const TRANSITION_CONFIG: Record<
  string,
  {
    icon: React.ReactNode;
    variant: 'primary' | 'success' | 'danger' | 'warning' | 'outline';
    requireNotes: boolean;
  }
> = {
  diajukan: { icon: <Send className="w-4 h-4" />, variant: 'primary', requireNotes: false },
  draf: { icon: <RotateCcw className="w-4 h-4" />, variant: 'outline', requireNotes: true },
  validasi_l1: { icon: <CheckCircle className="w-4 h-4" />, variant: 'success', requireNotes: false },
  ditolak_l1: { icon: <XCircle className="w-4 h-4" />, variant: 'danger', requireNotes: true },
  validasi_l2: { icon: <CheckCircle className="w-4 h-4" />, variant: 'success', requireNotes: false },
  ditolak_l2: { icon: <XCircle className="w-4 h-4" />, variant: 'danger', requireNotes: true },
  antrean_rapim: { icon: <Gavel className="w-4 h-4" />, variant: 'primary', requireNotes: false },
  diputuskan: { icon: <Flag className="w-4 h-4" />, variant: 'primary', requireNotes: false },
  berlangsung: { icon: <Play className="w-4 h-4" />, variant: 'warning', requireNotes: false },
  selesai: { icon: <CheckCircle className="w-4 h-4" />, variant: 'success', requireNotes: false },
  diarsipkan: { icon: <Archive className="w-4 h-4" />, variant: 'outline', requireNotes: false }};

// ========== HELPER FUNCTIONS ==========

/**
 * Determine validation level and action type based on current status and target state
 */
const getValidationContext = (
  currentStatus: string | undefined,
  toState: string
): {
  level: 'L1' | 'L2' | 'RAPIM' | 'OTHER';
  actionType: 'approve' | 'reject' | 'revisi' | 'other';
  nextStep: string;
} => {
  const status = (currentStatus || '').toLowerCase();
  const target = toState.toLowerCase();

  // Level 1 Validation (Dekan/Kalem)
  if (status === 'diajukan') {
    if (target === 'validasi_l1') {
      return { level: 'L1', actionType: 'approve', nextStep: 'Wakil Rektor (Level 2)' };
    }
    if (target === 'ditolak_l1') {
      return { level: 'L1', actionType: 'reject', nextStep: 'Pelapor' };
    }
    if (target === 'draf') {
      return { level: 'L1', actionType: 'revisi', nextStep: 'Pelapor untuk Revisi' };
    }
  }

  // Level 2 Validation (Warek)
  if (status === 'validasi_l1') {
    if (target === 'validasi_l2' || target === 'antrean_rapim') {
      return { level: 'L2', actionType: 'approve', nextStep: 'Antrean RAPIM' };
    }
    if (target === 'ditolak_l2') {
      return { level: 'L2', actionType: 'reject', nextStep: 'Pelapor' };
    }
    if (target === 'diajukan') {
      return { level: 'L2', actionType: 'revisi', nextStep: 'Dekan/Kalem (Level 1)' };
    }
  }

  // From validasi_l2 to antrean_rapim (alternative flow)
  if (status === 'validasi_l2' && target === 'antrean_rapim') {
    return { level: 'L2', actionType: 'approve', nextStep: 'Antrean RAPIM' };
  }

  // RAPIM decisions
  if (status === 'antrean_rapim') {
    if (target === 'diputuskan' || target === 'berlangsung') {
      return { level: 'RAPIM', actionType: 'approve', nextStep: 'Pelaksanaan Tindak Lanjut' };
    }
  }

  // Completion
  if (target === 'selesai') {
    return { level: 'OTHER', actionType: 'approve', nextStep: 'Selesai' };
  }

  return { level: 'OTHER', actionType: 'other', nextStep: '' };
};

/**
 * Get display label for transition
 */
const getDisplayLabel = (label?: string, toState?: string): string => {
  const state = (toState || '').toLowerCase();

  // Custom labels for specific states
  const customLabels: Record<string, string> = {
    validasi_l1: 'Setujui Level 1',
    validasi_l2: 'Setujui Level 2',
    ditolak_l1: 'Tolak Laporan',
    ditolak_l2: 'Tolak Laporan',
    antrean_rapim: 'Setujui ke RAPIM',
    draf: 'Minta Revisi',
    diajukan: 'Ajukan Laporan',
    selesai: 'Tandai Selesai',
    berlangsung: 'Mulai Tindak Lanjut',
    diarsipkan: 'Arsipkan'};

  return customLabels[state] || label || toState || '';
};

/**
 * Get modal title based on action type
 */
const getModalTitle = (
  actionType: 'approve' | 'reject' | 'revisi' | 'other',
  toState: string
): string => {
  switch (actionType) {
    case 'approve':
      return `Konfirmasi: ${getDisplayLabel(undefined, toState)}`;
    case 'reject':
      return 'Konfirmasi: Tolak Laporan';
    case 'revisi':
      return 'Konfirmasi: Minta Revisi';
    default:
      return `Konfirmasi: ${getDisplayLabel(undefined, toState)}`;
  }
};

/**
 * Get info banner message based on context
 */
const getInfoMessage = (
  level: 'L1' | 'L2' | 'RAPIM' | 'OTHER',
  actionType: 'approve' | 'reject' | 'revisi' | 'other',
  nextStep: string
): string => {
  if (actionType === 'approve') {
    switch (level) {
      case 'L1':
        return `Anda akan menyetujui laporan ini. Laporan akan diteruskan ke ${nextStep} untuk validasi selanjutnya.`;
      case 'L2':
        return `Anda akan menyetujui laporan ini. Laporan akan masuk ke ${nextStep} untuk dibahas dalam rapat pimpinan.`;
      case 'RAPIM':
        return `Laporan telah diputuskan dalam RAPIM dan akan dilanjutkan ke tahap ${nextStep}.`;
      default:
        return `Anda akan mengubah status laporan. ${nextStep ? `Selanjutnya: ${nextStep}` : ''}`;
    }
  }

  if (actionType === 'reject') {
    return 'Laporan akan ditolak dan dikembalikan ke pelapor. Pastikan Anda telah memberikan alasan penolakan yang jelas.';
  }

  if (actionType === 'revisi') {
    if (level === 'L2') {
      return `Laporan akan dikembalikan ke ${nextStep} untuk ditinjau ulang. Catatan wajib diisi.`;
    }
    return 'Laporan akan dikembalikan ke pelapor untuk diperbaiki. Catatan wajib diisi agar pelapor tahu yang harus diperbaiki.';
  }

  return 'Anda akan mengubah status laporan ini.';
};

// ========== COMPONENT ==========
const WorkflowActions: React.FC<Props> = ({
  transitions,
  loading,
  laporan,
  onChangeStatus}) => {
  const [selectedTransition, setSelectedTransition] = useState<AvailableTransition | null>(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Get validation context for selected transition
  const validationContext = useMemo(() => {
    if (!selectedTransition) return null;
    return getValidationContext(laporan?.status, selectedTransition.to_state);
  }, [selectedTransition, laporan?.status]);

  const openModal = (t: AvailableTransition) => {
    setSelectedTransition(t);
    setNotes('');
  };

  const closeModal = () => {
    setSelectedTransition(null);
    setNotes('');
  };

  const handleConfirm = async () => {
    if (!selectedTransition) return;
    const config = TRANSITION_CONFIG[selectedTransition.to_state];
    if (config?.requireNotes && !notes.trim()) return;

    setProcessing(true);
    try {
      await onChangeStatus(selectedTransition.to_state, notes || undefined);
      closeModal();
    } finally {
      setProcessing(false);
    }
  };

  if (loading || transitions.length === 0) return null;

  const config = selectedTransition
    ? TRANSITION_CONFIG[selectedTransition.to_state]
    : null;

  const toState = selectedTransition?.to_state || '';
  const actionType = validationContext?.actionType || 'other';
  const level = validationContext?.level || 'OTHER';
  const nextStep = validationContext?.nextStep || '';

  const isApprove = actionType === 'approve';
  const isReject = actionType === 'reject';
  const isRevisi = actionType === 'revisi';

  // Determine button variant based on action
  const buttonVariant = isApprove
    ? 'success'
    : isReject
      ? 'danger'
      : isRevisi
        ? 'warning'
        : config?.variant || 'primary';

  return (
    <>
      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-2">
        {transitions.map((t) => {
          const cfg = TRANSITION_CONFIG[t.to_state] || {
            icon: <ArrowRight className="w-4 h-4" />,
            variant: 'outline' as const};
          return (
            <Button
              key={t.to_state}
              variant={cfg.variant}
              size="sm"
              icon={cfg.icon}
              onClick={() => openModal(t)}
            >
              {getDisplayLabel(t.label, t.to_state)}
            </Button>
          );
        })}
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={!!selectedTransition}
        onClose={closeModal}
        title={getModalTitle(actionType, toState)}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={closeModal}>
              Batal
            </Button>
            <Button
              variant={buttonVariant}
              onClick={handleConfirm}
              loading={processing}
              disabled={config?.requireNotes && !notes.trim()}
            >
              {getDisplayLabel(selectedTransition?.label, toState)}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Info Banner */}
          <div
            className={`p-3 rounded-xl border text-sm font-medium flex items-start gap-3 ${isApprove
                ? 'bg-success/5 border-success/20 text-success'
                : isReject
                  ? 'bg-danger/5 border-danger/20 text-danger'
                  : isRevisi
                    ? 'bg-warning/5 border-warning/20 text-warning'
                    : 'bg-info/5 border-info/20 text-info'
              }`}
          >
            {isApprove && <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />}
            {isReject && <XCircle className="w-5 h-5 shrink-0 mt-0.5" />}
            {isRevisi && <RotateCcw className="w-5 h-5 shrink-0 mt-0.5" />}
            {!isApprove && !isReject && !isRevisi && (
              <ArrowRight className="w-5 h-5 shrink-0 mt-0.5" />
            )}
            <span>{getInfoMessage(level, actionType, nextStep)}</span>
          </div>

          {/* Report Card */}
          {laporan && (
            <div className="p-4 bg-neutral-bg rounded-xl space-y-2">
              <p className="text-xs font-mono text-teal-main">{laporan.kode || '-'}</p>
              <p className="text-sm font-bold text-neutral-text-primary">
                {laporan.judul || '-'}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                {laporan.tingkat_risiko && (
                  <Badge variant="risk" risk={laporan.tingkat_risiko} />
                )}
                {laporan.unit?.name && (
                  <span className="text-xs text-neutral-text-secondary flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {laporan.unit.name}
                  </span>
                )}
                {laporan.user?.name && (
                  <span className="text-xs text-neutral-text-secondary flex items-center gap-1">
                    <User className="w-3 h-3" /> {laporan.user.name}
                  </span>
                )}
              </div>
              {laporan.deskripsi && (
                <p className="text-xs text-neutral-text-secondary mt-2 line-clamp-3">
                  {laporan.deskripsi}
                </p>
              )}
            </div>
          )}

          {/* Validation Path - Level 1 Approval */}
          {isApprove && level === 'L1' && (
            <div className="flex items-center justify-center gap-2 py-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                <CheckCircle className="w-3.5 h-3.5" /> Dekan Setuju
              </div>
              <div className="w-6 h-0.5 bg-neutral-border hidden sm:block" />
              <ArrowRight className="w-4 h-4 text-neutral-border sm:hidden" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                <Clock className="w-3.5 h-3.5" /> Menunggu Warek
              </div>
              <div className="w-6 h-0.5 bg-neutral-border hidden sm:block" />
              <ArrowRight className="w-4 h-4 text-neutral-border sm:hidden" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                <Gavel className="w-3.5 h-3.5" /> RAPIM
              </div>
            </div>
          )}

          {/* Validation Path - Level 2 Approval */}
          {isApprove && level === 'L2' && (
            <div className="flex items-center justify-center gap-2 py-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                <CheckCircle className="w-3.5 h-3.5" /> Dekan ✓
              </div>
              <div className="w-6 h-0.5 bg-success hidden sm:block" />
              <ArrowRight className="w-4 h-4 text-success sm:hidden" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                <CheckCircle className="w-3.5 h-3.5" /> Warek Setuju
              </div>
              <div className="w-6 h-0.5 bg-neutral-border hidden sm:block" />
              <ArrowRight className="w-4 h-4 text-neutral-border sm:hidden" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                <Clock className="w-3.5 h-3.5" /> RAPIM
              </div>
            </div>
          )}

          {/* Validation Path - RAPIM Decision */}
          {isApprove && level === 'RAPIM' && (
            <div className="flex items-center justify-center gap-2 py-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                <CheckCircle className="w-3.5 h-3.5" /> Dekan ✓
              </div>
              <div className="w-4 h-0.5 bg-success hidden sm:block" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                <CheckCircle className="w-3.5 h-3.5" /> Warek ✓
              </div>
              <div className="w-4 h-0.5 bg-success hidden sm:block" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                <Gavel className="w-3.5 h-3.5" /> RAPIM Diputuskan
              </div>
              <div className="w-4 h-0.5 bg-neutral-border hidden sm:block" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                <Play className="w-3.5 h-3.5" /> Tindak Lanjut
              </div>
            </div>
          )}

          {/* Revisi Path - L2 to L1 */}
          {isRevisi && level === 'L2' && (
            <div className="flex items-center justify-center gap-2 py-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-warning/10 text-warning text-xs font-bold">
                <RotateCcw className="w-3.5 h-3.5" /> Dikembalikan
              </div>
              <div className="w-6 h-0.5 bg-warning hidden sm:block" />
              <ArrowRight className="w-4 h-4 text-warning sm:hidden" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                <Shield className="w-3.5 h-3.5" /> Dekan/Kalem Review
              </div>
            </div>
          )}

          {/* Textarea for Notes */}
          <Textarea
            label={config?.requireNotes ? 'Catatan (wajib)' : 'Catatan (opsional)'}
            placeholder={
              isReject
                ? 'Jelaskan alasan penolakan secara detail...'
                : isRevisi
                  ? 'Jelaskan apa yang perlu direvisi atau diperbaiki...'
                  : 'Tambahkan catatan jika diperlukan...'
            }
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            required={config?.requireNotes}
            rows={4}
          />

          {/* Warning for required notes */}
          {config?.requireNotes && !notes.trim() && (
            <p className="text-xs text-danger flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              Catatan wajib diisi untuk melanjutkan
            </p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default WorkflowActions;